import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../context/AppContext";
import { resetPassword } from "../api/users";

const SetPassword = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const { token } = useParams();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [form, setForm] = useState({ password: "", confirmPassword: "" });
    const [errors, setErrors] = useState({ password: "", confirmPassword: "" });

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const validatePassword = (password) => {
        return /^(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(password);
    };

    const handleChangePassword = async () => {
        setIsLoading(true);

        let newErrors = { password: "", confirmPassword: "" };

        if (!form.password.trim()) {
            newErrors.password = t("errors.required_password");
        } else if (!validatePassword(form.password)) {
            newErrors.password = t("errors.weak_password");
        }

        if (!form.confirmPassword.trim()) {
            newErrors.confirmPassword = t("errors.required_retype_password");
        } else if (form.password !== form.confirmPassword) {
            newErrors.confirmPassword = t("errors.passwords_do_not_match");
        }

        const hasErrors = newErrors.password || newErrors.confirmPassword;
        setErrors(newErrors);

        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        try {
            await resetPassword(token, form.password);
            navigate("/login");
        } catch (error) {
            console.error("Password change failed:", error);
            setErrors({
                password: t("errors.resetFailed"),
                confirmPassword: "",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            navigate("/login");
        }
    }, [token, navigate]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: theme.palette.background.default,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
            }}
        >
            <Card
                sx={{
                    maxWidth: 400,
                    width: "100%",
                    bgcolor: theme.palette.sidebar?.background || theme.palette.background.paper,
                    boxShadow: 6,
                    borderRadius: 2,
                }}
            >
                <CardContent>
                    <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
                        {t("setPassword.title")}
                    </Typography>

                    <form onSubmit={(e) => {
                        e.preventDefault();
                        handleChangePassword();
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label={t("setPassword.newPassword")}
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={handleChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            sx={{ mb: 2 }}
                            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                            InputProps={{
                                style: { color: theme.palette.text.primary },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            variant="outlined"
                            label={t("setPassword.confirmPassword")}
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={form.confirmPassword}
                            onChange={handleChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            sx={{ mb: 3 }}
                            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                            InputProps={{
                                style: { color: theme.palette.text.primary },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            type="submit"
                            sx={{
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 2,
                            }}
                        >
                            {t("setPassword.setPasswordButton")}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SetPassword;
